/**
 * Login — the Meridian sign-in hero.
 *
 * Auth is the BFF/OIDC pattern (decision #1): no username/password form. The card's
 * "Sign in" launches the server-side flow (GET /auth/login -> Keycloak -> back), and useMe()
 * hydrates the session on return. An existing session bounces straight into the app.
 *
 * Design: a committed dark hero (independent of app theme), built around Meridian's own
 * motif -- a slow violet aurora drifting over a "meridian" great-circle arc, rendered on
 * canvas. Original marks + motion + palette; copies no OEM login. Honors prefers-reduced-
 * motion by drawing a single static frame.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { beginLogin } from "@/lib/api/client";
import { loginRouteDecision, probeMe } from "@/lib/loginRedirect";
import { useSession } from "@/stores/session";

// Committed hero palette (explicit, theme-independent). Violet aurora over a WARM charcoal
// ground with warm-ivory text (the whites are intentionally warm, not violet-tinted).
const GROUND = "#12100D";
const INK = "#F3EEE3";        // warm ivory — headings/wordmark
const INK_BODY = "#EAE3D6";   // warm off-white — body
const INK_MUTED = "#C6BEAF";  // warm grey — subtext
const INK_SUBTLE = "#8C8578"; // warm grey — captions
const BLOBS = [
  { x: 0.30, y: 0.22, r: 0.55, color: [91, 75, 224], amp: 0.05, speed: 0.11, phase: 0.0 }, // brand violet
  { x: 0.72, y: 0.30, r: 0.50, color: [59, 46, 143], amp: 0.06, speed: 0.08, phase: 1.7 }, // indigo
  { x: 0.58, y: 0.68, r: 0.48, color: [138, 126, 245], amp: 0.05, speed: 0.13, phase: 3.1 }, // lifted violet
  { x: 0.18, y: 0.72, r: 0.42, color: [79, 214, 224], amp: 0.07, speed: 0.10, phase: 4.6 }, // cool cyan highlight
  { x: 0.86, y: 0.74, r: 0.40, color: [181, 75, 224], amp: 0.06, speed: 0.09, phase: 2.3 }, // magenta edge
];

/** Full-bleed animated aurora + meridian arc. Self-contained canvas effect. */
function MeridianAurora() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let w = 0;
    let h = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (t: number) => {
      // Ground.
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = GROUND;
      ctx.fillRect(0, 0, w, h);

      // Drifting aurora blobs (additive glow).
      ctx.globalCompositeOperation = "lighter";
      const unit = Math.hypot(w, h);
      for (const b of BLOBS) {
        const cx = (b.x + Math.sin(t * b.speed + b.phase) * b.amp) * w;
        const cy = (b.y + Math.cos(t * b.speed * 0.9 + b.phase) * b.amp) * h;
        const rad = b.r * unit * 0.5;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
        const [r, gg, bb] = b.color;
        g.addColorStop(0, `rgba(${r},${gg},${bb},0.42)`);
        g.addColorStop(0.5, `rgba(${r},${gg},${bb},0.14)`);
        g.addColorStop(1, `rgba(${r},${gg},${bb},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, Math.PI * 2);
        ctx.fill();
      }

      // The "meridian" — a great-circle arc sweeping the composition.
      ctx.globalCompositeOperation = "screen";
      const arc = ctx.createLinearGradient(0, 0, w, h);
      arc.addColorStop(0, "rgba(138,126,245,0)");
      arc.addColorStop(0.5, "rgba(160,150,255,0.5)");
      arc.addColorStop(1, "rgba(79,214,224,0)");
      ctx.strokeStyle = arc;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      // A wide, shallow ellipse arc; the slow phase makes it breathe.
      const cx = w * 0.62;
      const cy = h * (0.52 + Math.sin(t * 0.05) * 0.01);
      ctx.ellipse(cx, cy, w * 0.7, h * 0.42, -0.32, Math.PI * 0.9, Math.PI * 1.9);
      ctx.stroke();

      // Vignette to seat the composition.
      ctx.globalCompositeOperation = "source-over";
      const vg = ctx.createRadialGradient(w * 0.5, h * 0.5, h * 0.3, w * 0.5, h * 0.5, h * 0.9);
      vg.addColorStop(0, "rgba(18,16,13,0)");
      vg.addColorStop(1, "rgba(10,9,7,0.78)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);

      if (!reduce) raf = requestAnimationFrame(() => draw(t + 0.016));
    };

    resize();
    draw(0);
    const onResize = () => {
      resize();
      if (reduce) draw(0);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden />;
}

export default function Login() {
  const navigate = useNavigate();
  const me = useSession((s) => s.me);
  const orgId = useSession((s) => s.currentOrgId);

  useEffect(() => {
    if (me && orgId) navigate(`/o/${orgId}`, { replace: true });
  }, [me, orgId, navigate]);

  // Already signed in but the in-memory store is empty (fresh page load on /login, e.g. a
  // bookmark or the back button after signing in)? Ask the server. A real /api/v2/me 200
  // goes straight into the app instead of offering a second sign-in -- the 2026-10-08
  // incident started with exactly that redundant login. See lib/loginRedirect.
  useEffect(() => {
    if (me) return; // the effect above already handles a hydrated session
    const ctl = new AbortController();
    void probeMe((url, init) => fetch(url, init), ctl.signal).then((result) => {
      if (!ctl.signal.aborted && loginRouteDecision(result) === "go_home") {
        navigate("/", { replace: true });
      }
    });
    return () => ctl.abort();
  }, [me, navigate]);

  return (
    <div className="relative min-h-dvh overflow-hidden" style={{ background: GROUND, color: INK_BODY }}>
      <MeridianAurora />

      {/* Content grid: brand lockup (left) + glass auth card (right). */}
      <div className="relative mx-auto flex min-h-dvh max-w-6xl flex-col justify-center gap-12 px-6 py-12 md:flex-row md:items-center md:justify-between md:px-12">
        {/* Brand lockup */}
        <div className="max-w-md animate-[fadeUp_0.7s_ease-out]">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#5B4BE0,#8A7EF5)" }}>
              <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden>
                <path d="M4 18 12 5l8 13" fill="none" stroke={GROUND} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
            </span>
            <span className="text-2xl font-semibold tracking-[0.18em] uppercase" style={{ color: INK }}>
              Meridian
            </span>
          </div>
          <h1 className="mt-8 text-balance text-4xl font-semibold leading-tight md:text-5xl" style={{ color: INK }}>
            Operate every site as one.
          </h1>
          <p className="mt-4 max-w-sm text-base leading-relaxed" style={{ color: INK_MUTED }}>
            One controller for wireless, switching, and guest access — running on your own
            infrastructure, not someone else&rsquo;s cloud.
          </p>
        </div>

        {/* Glass auth card */}
        <div
          className="w-full max-w-sm animate-[fadeUp_0.7s_ease-out_0.1s_both] rounded-2xl border p-8 shadow-2xl backdrop-blur-xl"
          style={{
            background: "rgba(28,25,21,0.55)",
            borderColor: "rgba(138,126,245,0.28)",
            boxShadow: "0 24px 70px -20px rgba(0,0,0,0.7)",
          }}
        >
          <h2 className="text-lg font-semibold" style={{ color: INK }}>
            Sign in
          </h2>
          <p className="mt-1 text-sm" style={{ color: INK_MUTED }}>
            Use your operator account to continue.
          </p>

          <button
            type="button"
            // No return_to: this page's own path is /login, and landing back on /login after
            // signing in would show this card again (the server also refuses /login now).
            onClick={() => beginLogin()}
            className="mt-6 w-full rounded-lg px-4 py-3 text-sm font-semibold transition-[filter] hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              background: "linear-gradient(135deg,#5B4BE0,#8A7EF5)",
              color: "#14110C",
              // @ts-expect-error CSS custom prop for the focus ring offset color
              "--tw-ring-color": "#8A7EF5",
              "--tw-ring-offset-color": GROUND,
            }}
          >
            Sign in
          </button>

          <p className="mt-4 text-xs" style={{ color: INK_SUBTLE }}>
            You&rsquo;ll be redirected to your identity provider.
          </p>
        </div>
      </div>

      {/* Local keyframes for the entrance (scoped to this page). */}
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        @media (prefers-reduced-motion: reduce){.animate-\\[fadeUp_0\\.7s_ease-out\\],.animate-\\[fadeUp_0\\.7s_ease-out_0\\.1s_both\\]{animation:none}}`}</style>
    </div>
  );
}
